/**
 * InstallPrompt — Apple-style bottom banner that invites the user to add the
 * app to their home screen.
 *
 * • On Chrome/Edge/Android we capture the native `beforeinstallprompt` event
 *   and expose a button that triggers `prompt()`.
 * • On iOS Safari (no `beforeinstallprompt` support) we show an instruction
 *   with the Share → "Ana Ekrana Ekle" hint.
 * • Auto-hides when the app is already installed (`display-mode: standalone`)
 *   or when the user dismissed the banner in the last 30 days.
 */
import { useEffect, useState } from "react";
import { X, Smartphone, Share } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "akb.pwa.install.dismissed_at";
const DISMISS_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function isIos() {
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function wasDismissedRecently() {
  const at = localStorage.getItem(DISMISS_KEY);
  if (!at) return false;
  const ts = Number(at);
  return !Number.isNaN(ts) && Date.now() - ts < DISMISS_TTL_MS;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const iOS = isIos();

  useEffect(() => {
    if (isStandalone() || wasDismissedRecently()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setVisible(true), 1500);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS never fires beforeinstallprompt — show the manual instruction after a delay.
    if (iOS) {
      const t = setTimeout(() => setVisible(true), 3000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, [iOS]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try { await deferredPrompt.userChoice; } catch { /* ignore */ }
    setDeferredPrompt(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed z-40 left-3 right-3 md:left-auto md:right-6 md:bottom-6 md:w-[380px]"
      style={{ bottom: "max(0.75rem, calc(env(safe-area-inset-bottom) + 0.75rem))" }}
      role="dialog"
      aria-labelledby="pwa-install-title"
      data-testid="pwa-install-banner"
    >
      <div className="apple-card p-4 shadow-elevated animate-apple-slide-up">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Smartphone className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div id="pwa-install-title" className="font-semibold text-[15px] tracking-tight">
              AKB Karavan'ı ana ekrana ekle
            </div>
            <p className="text-[13px] text-muted-foreground mt-1 leading-snug">
              {iOS
                ? "Alt paylaş ikonuna dokunun ve “Ana Ekrana Ekle”yi seçin. Tam ekran, hızlı, yerel uygulama gibi çalışır."
                : "Tek dokunuşla telefonunuzun ana ekranına ekleyin, uygulama gibi tam ekran açılsın."}
            </p>
            {iOS ? (
              <div className="mt-3 flex items-center gap-2 text-[12px] text-muted-foreground bg-muted/60 rounded-lg px-2.5 py-2">
                <Share className="w-4 h-4 text-primary shrink-0" />
                Safari alt menüsündeki paylaş simgesi → “Ana Ekrana Ekle”
              </div>
            ) : (
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={install} data-testid="pwa-install-btn">
                  <Smartphone className="w-4 h-4 mr-1.5" />
                  Ana Ekrana Ekle
                </Button>
                <Button size="sm" variant="ghost" onClick={dismiss} data-testid="pwa-install-later-btn">
                  Sonra
                </Button>
              </div>
            )}
          </div>
          <button
            onClick={dismiss}
            aria-label="Kapat"
            className="text-muted-foreground hover:text-foreground apple-ease transition-colors -mt-1 -mr-1 p-1"
            data-testid="pwa-install-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
